<script setup lang="ts">
import { CHALLENGE_PRIZE_TYPE, type PrizePoolAmount } from '@/types/shared/player-config'
import { computed } from 'vue'
import GoldMedalImage from '@/assets/images/medal-gold.png'
import SilverMedalImage from '@/assets/images/medal-silver.png'
import BronzeMedalImage from '@/assets/images/medal-bronze.png'
import StarMedalImage from '@/assets/images/medal-star.png'
import { formatNumberFullTruncate, toOrdinal } from '@/helpers/utils'
import { Vue3Lottie } from 'vue3-lottie'
import { availableBadges } from '@/composables/game/badge'
import { useGiftModal } from '@/composables/game/gift-modal'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'

const props = defineProps<{
	prizeType: CHALLENGE_PRIZE_TYPE
	prizeItem: PrizePoolAmount
	prizeImage: string
}>()

const { setSelectedGift } = useGiftModal()

const circleStrokeClass = computed(() => {
  const secondPosition = props.prizeItem.toRank
  switch (secondPosition) {
  case 1:
    return 'bg-gradient-gold'
  case 2:
    return 'bg-[#9D9D9D]'
  case 3:
    return 'bg-[#923906]'
  default:
    return 'bg-[#727274]'
  }
})

const positionImage = computed(() => {
  const firstPosition = props.prizeItem.fromRank
  switch (firstPosition) {
  case 1:
    return GoldMedalImage
  case 2:
    return SilverMedalImage
  case 3:
    return BronzeMedalImage
  default:
    return StarMedalImage
  }
})

const positionLabel = computed(() => {
  const firstPosition = props.prizeItem.fromRank
  const secondPosition = props.prizeItem.toRank
  if (firstPosition === secondPosition) {
    return toOrdinal(firstPosition)
  }
  return `${toOrdinal(firstPosition)} - ${toOrdinal(secondPosition)}`
})

const badge = computed(() => availableBadges.find((badge) => badge.id === props.prizeItem.badgeId))
</script>

<template>
  <div class="flex w-full items-center justify-between py-2">
    <div class="flex flex-1 items-center gap-x-3">
      <div
        class="size-[42px] min-w-[42px] p-0.5 rounded-full"
        :class="circleStrokeClass"
      >
        <div class="bg-dark-top-layer flex items-center justify-center w-full h-full rounded-full">
          <img
            :src="positionImage"
            class="size-6"
          >
        </div>
      </div>
      <div class="text-base font-semibold -tracking-[0.32px]">
        {{ positionLabel }}
      </div>
    </div>
    <div class="ml-6 flex items-center gap-x-2">
      <VTooltip
        v-if="badge"
        placement="top"
        class="max-w-fit"
      >
        <template #popper>
          {{ badge.name }}
        </template>
        <img
          class="size-6 min-w-6 object-cover"
          :src="badge.imageUrl"
        >
      </VTooltip>
      <template v-if="'specialCurrencyId' in prizeItem && 'amount' in prizeItem">
        <div class="text-white text-lg font-medium float-right">
          {{ formatNumberFullTruncate(prizeItem.amount) }}
        </div>
        <img
          :src="allSpecialCurrenciesConfig[prizeItem.specialCurrencyId]?.image"
          class="size-6 min-w-[24px] object-cover"
        >
      </template>
      <template v-else-if="'amount' in prizeItem">
        <div class="text-white text-lg font-medium float-right">
          {{ formatNumberFullTruncate(prizeItem.amount) }}
        </div>
        <img
          :src="prizeImage"
          class="size-6 min-w-[24px] object-cover"
        >
      </template>
      <div
        v-else-if="'nft' in prizeItem"
        class="size-10"
        @click="setSelectedGift({ name: prizeItem.nft, lottie: prizeItem.lottieUrl })"
      >
        <Vue3Lottie
          class="rounded-lg"
          :animation-link="prizeItem.lottieUrl"
          :auto-play="true"
          :loop="true"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.bg-gradient-gold {
	background: linear-gradient(
			to right,
			#fff246 0%,
			#e5b80b 37%,
			#fff571 69%,
			#e5b80b 100%
	);
}
</style>